/**
 * Calendar-day arithmetic in the tournament's own timezone. Every game time is
 * stored as a UTC instant and shown in `tournament.timezone`, so "today" and
 * "the day of this game" have to be asked in that zone — a 7 pm game in
 * Chicago is tomorrow in UTC.
 *
 * Days are keyed "YYYY-MM-DD" in that zone; a game with no usable time keys
 * to `UNSCHEDULED`. Pure, so the Scores page's strip can be tested as data.
 */
import { isoToWallClock } from "./datetime";
import { venueLabel } from "./gameLocation";
import type { Game, Tournament, Venue } from "./types";

/** The day key for games that have no time yet. Sorts after every real day. */
export const UNSCHEDULED = "unscheduled";

const DAY_MS = 86_400_000;
/** A date range longer than this is bad data, not a tournament. */
const MAX_RANGE_DAYS = 60;

/** "2026-05-02": the day `iso` falls on in `timeZone`. Null for no time or an unparsable one. */
export function dayKey(iso: string | undefined, timeZone: string): string | null {
  const wall = isoToWallClock(iso, timeZone);
  return wall ? wall.slice(0, 10) : null;
}

/** The day a game keys under: its day in the zone, or `UNSCHEDULED`. */
export function gameDayKey(game: Pick<Game, "scheduledAt">, timeZone: string): string {
  return dayKey(game.scheduledAt, timeZone) ?? UNSCHEDULED;
}

/** A date-only string ("2026-05-01") as UTC midnight; NaN when unreadable. */
const parseDay = (s: string): number => Date.parse(s.includes("T") ? s : `${s}T00:00:00Z`);

/**
 * Every day the strip should offer: the tournament's date range, plus any day
 * a game actually falls on (a Friday-night opener before the listed start),
 * sorted, with `UNSCHEDULED` last when a game has no time.
 */
export function tournamentDays(
  t: Pick<Tournament, "startDate" | "endDate" | "timezone">,
  games: Pick<Game, "scheduledAt">[],
): string[] {
  const days = new Set<string>();
  const start = parseDay(t.startDate);
  if (!Number.isNaN(start)) {
    const end = parseDay(t.endDate);
    const last = Number.isNaN(end) || end < start ? start : Math.min(end, start + MAX_RANGE_DAYS * DAY_MS);
    for (let ms = start; ms <= last; ms += DAY_MS) days.add(new Date(ms).toISOString().slice(0, 10));
  }
  let unscheduled = false;
  for (const g of games) {
    const key = dayKey(g.scheduledAt, t.timezone);
    if (key) days.add(key);
    else unscheduled = true;
  }
  const sorted = [...days].sort();
  return unscheduled ? [...sorted, UNSCHEDULED] : sorted;
}

/** A game's instant for ordering; a timeless game sorts after every timed one. */
const timeOf = (g: Pick<Game, "scheduledAt">): number => {
  const ms = g.scheduledAt ? Date.parse(g.scheduledAt) : NaN;
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
};

/** Games by day key, each day's list in time order (then by id, for a stable page). */
export function groupByDay(games: Game[], timeZone: string): Map<string, Game[]> {
  const byDay = new Map<string, Game[]>();
  const sorted = [...games].sort((a, b) => timeOf(a) - timeOf(b) || a.id.localeCompare(b.id));
  for (const g of sorted) {
    const key = gameDayKey(g, timeZone);
    const list = byDay.get(key);
    if (list) list.push(g);
    else byDay.set(key, [g]);
  }
  return byDay;
}

/**
 * The day the Scores page opens on when the URL names none (console spec
 * §5.5): today if it is a listed day; else the first listed day with something
 * to score; else the first day. Undefined only when there are no days at all.
 */
export function defaultDay(
  days: string[],
  playableDays: ReadonlySet<string>,
  today: string,
): string | undefined {
  if (days.includes(today)) return today;
  return days.find((d) => playableDays.has(d)) ?? days[0];
}

/** The distinct places a day's games are played, for the field chips. */
export function fieldsOn(games: Game[], venues: Venue[]): string[] {
  const labels = new Set(games.map((g) => venueLabel(venues, g)).filter(Boolean));
  return [...labels].sort((a, b) => a.localeCompare(b));
}

/**
 * "Sat, May 2" for a day key, formatted in UTC so the key is never shifted by
 * the viewer's own zone; "Unscheduled" for the games without a time.
 */
export function dayLabel(day: string): string {
  if (day === UNSCHEDULED) return "Unscheduled";
  const ms = parseDay(day);
  if (Number.isNaN(ms)) return day;
  return new Date(ms).toLocaleDateString(undefined, {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
